import React from 'react';
import { makeCSS } from '../../../utils/makeCSS';
import { cx } from '@emotion/css';
import { logoFiles } from './files';
import { SizeVariants, Theme } from '../../../theme/themes';
import { useClassName } from '../../../utils/useClassName';
import theme from '@cube3/design-tokens/build/js/dark-tokens';

// eslint-disable-next-line @typescript-eslint/no-empty-interface

/*
 * Visual indicator that showcases the different logos of the brands that can be used inside Cube and the colors they can take.
 * The logos can have their default color, white or black
 */
export type LogoName = keyof typeof logoFiles;

export interface BrandLogoProps {
  /**The name of the logos brand */
  logo?: string;
  /**The colors the logo can have */
  color?: 'default' | 'white' | 'black';
  /**Different sizes that the icon can have */
  size?:
    | SizeVariants.xs
    | SizeVariants.sm
    | SizeVariants.md
    | SizeVariants.lg
    | SizeVariants.xl
    | (typeof SizeVariants)['2xl']
    | (typeof SizeVariants)['3xl']
    | (typeof SizeVariants)['4xl']
    | (typeof SizeVariants)['5xl']
    | (typeof SizeVariants)['6xl']
    | (typeof SizeVariants)['7xl'];
  customWidth?: string | number;
}

const useCSS = makeCSS(
  ({ theme, customWidth = 64 }: BrandLogoProps & { theme: Theme }) => {
    return {
      root: {
        display: 'flex',
        flexShrink: 0,
        lineHeight: '1em',
        verticalAlign: 'middle',
        height: 'auto'
      },
      'size-xs': {
        width: theme.spacing[3]
      },
      'size-sm': {
        width: theme.spacing[4]
      },
      'size-md': {
        width: theme.spacing[5]
      },
      'size-lg': {
        width: theme.spacing[6]
      },
      'size-xl': {
        width: theme.spacing[7]
      },
      'size-2xl': {
        width: theme.spacing[8]
      },
      'size-3xl': {
        width: theme.spacing[10]
      },
      'size-4xl': {
        width: theme.spacing[12]
      },
      'size-5xl': {
        width: theme.spacing[14]
      },
      'size-6xl': {
        width: theme.spacing[16]
      },
      'size-7xl': {
        width: theme.spacing[20]
      },
      customWidth: {
        width:
          typeof customWidth === 'string'
            ? customWidth
            : theme.spacing[customWidth]
      }
    };
  }
);

export const BrandLogo = (props: BrandLogoProps) => {
  const { color = 'default', size = SizeVariants.md, customWidth } = props;
  let { logo: name } = props;

  const classes = useCSS(props);
  let newName;
  const defaultName = name + '_Brand';

  /**change the logo svg according to the background color */
  if (color === 'black') {
    newName = name + '_Black';
    /**if brand only has a default logo and not a black logo */
    if (!logoFiles[newName]) {
      newName = name + '_Brand';
    }
  } else if (color === 'white') {
    newName = name + '_White';
    /**if brand if brand only has a default logo and not a white logo */
    if (!logoFiles[newName]) {
      newName = name + '_Brand';
    }
  } else {
    newName = defaultName;
  }
  /** if newName does not exist in the folder of the logos*/
  if (logoFiles[newName]) {
    name = newName;
  } else {
    console.log('Brand logo does not exist.');
  }

  const Logo = logoFiles[name];
  if (!Logo) {
    return null;
  }

  return (
    <Logo
      className={useClassName(
        classes.root,
        customWidth ? classes.customWidth : classes[`size-${size}`],
        classes[color]
      )}
    ></Logo>
  );
};
